
import {
  Component,
  namespace,
  State,
  Vue,
  Watch
} from 'nuxt-property-decorator';
import CloseBtn from '../side-panel/CloseBtn.vue';
import { SidePanelMode } from '../side-panel/SidePanelMode';
import CustomSwitch from '../CustomSwitch.vue';
import { NotesClickedFrom, NotesMode } from '../side-panel/notes/NotesMode';
import TooltipButton from '../TooltipButton.vue';
import { pushEvent } from '@/util/grafana-faro';
import KeyHandleEventBus from '@/store/keyHandle.eventbus';
import UserSettingsStore, { Bookmark } from '@/store/userSettings.store';
import { NoteHighlight } from '@/types/highlightContract';
import NotesStore from '@/store/notes.store';
import { Metadata, Sso, User, Site } from '@/types/initialData';
import ClientSettingsStore from '@/store/clientSettings.store';
import LocalConfigStore from '@/store/localConfig.store';
import ToastService from '@/components/ui/toast/ToastService';
import FeatureGalleryStore from '@/components/ui/feature-gallery/feature-gallery.store';
import CookieStore from '@/components/ui/cookies/cookies.store';
import SidePanelStore from '@/components/ui/side-panel/side-panel.store';
import MostRecentlyUsedList from '@/components/ui/user-settings/most-recently-used/MostRecentlyUsedList.vue';
import LanguageSwitch from '@/components/ui/layout/LanguageSwitch.vue';
import { externalTypo3Url, getSignOutUrl } from '@/util/typo3Urls';

const highlightsModule = namespace('highlights.store');

@Component({
  components: {
    CloseBtn,
    TooltipButton,
    CustomSwitch,
    MostRecentlyUsedList,
    LanguageSwitch
  }
})
export default class UserMenu extends Vue {
  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.sso)
  sso: Sso;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.metadata)
  metadata: Metadata;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.user)
  user: User;

  // @ts-ignore
  @State((state) => !!state.typo3?.initial?.site?.user?.info?.id)
  isLoggedIn: boolean;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site)
  site: Site;

  @highlightsModule.Getter
  highlightsForIsbn: NoteHighlight[];

  menuOpen: boolean = false;
  showFocusModePrompt: boolean = false;
  dontShowAgain: boolean = false;
  focusModeSwitch: boolean = false;

  get currentDarkMode(): boolean {
    return ClientSettingsStore.darkMode;
  }

  get urlAccountHome(): string {
    return this.sso ? this.sso.url_account_home : '';
  }

  get isSystimeMyAccount(): boolean {
    return this.isLoggedIn && this.sso.myaccountId === 'SYSTIMEMYACCOUNT';
  }

  get isStudyboxAccount(): boolean {
    return this.isLoggedIn && this.sso.myaccountId === 'STUDYBOXMYACCOUNT';
  }

  get accountLinkLabel(): string {
    const accountLabel =
      this.$tc('labels.usermenu.related.other-publications') +
      ' ' +
      this.$tc('labels.generic.at') +
      ' ' +
      (this.isStudyboxAccount
        ? this.$tc('labels.generic.studyboxaccount')
        : this.$tc('labels.generic.myaccount'));

    return accountLabel;
  }

  get userIsValidatedTeacher(): boolean {
    return this.user.info && this.user.info.usergroups.includes('21');
  }

  get bookmarks(): Bookmark[] {
    return UserSettingsStore.bookmarks;
  }

  get metaDataTitle(): string {
    return this.metadata ? this.metadata.title : '';
  }

  get privacyLink(): string {
    return this.metadata ? this.metadata.links.privacyurl : '';
  }

  get userName(): string {
    return this.user && this.user.info ? this.user.info.userName : '';
  }

  get userEmail(): string {
    return this.user && this.user.info ? this.user.info.email : '';
  }

  get focusModeIsSet(): boolean {
    return ClientSettingsStore.focusMode;
  }

  get skipPrompt(): boolean {
    return ClientSettingsStore.hideFocusModePrompt;
  }

  get totalHighlightsCount(): number {
    return this.highlightsForIsbn ? this.highlightsForIsbn.length : 0;
  }

  get showAsStudentMode(): boolean {
    return LocalConfigStore.showAsStudent;
  }

  get techFeedBackLink(): string {
    return this.metadata && this.metadata.links
      ? this.metadata.links.techFeedbackLink
      : '';
  }

  // We use this to handle restricted users
  get isIpAccess(): boolean {
    return LocalConfigStore.isIpAccess;
  }

  @Watch('focusModeIsSet')
  onFocusModeChange(val: boolean): void {
    if (!val) {
      this.showFocusModePrompt = val;
      this.focusModeSwitch = val;
    }
  }

  mounted() {
    UserMenu.detectDarkModeFromOSSettings();
    if (this.$vuetify.theme.dark) {
      ClientSettingsStore.changeDarkMode(true, this.site);
    }
    KeyHandleEventBus.$on('escape', this.escPressedHandler);
  }

  destroyed() {
    KeyHandleEventBus.$off('escape', this.escPressedHandler);
  }

  escPressedHandler() {
    this.menuOpen = false;
  }

  showFeatureGallery(): void {
    this.menuOpen = false;
    FeatureGalleryStore.showModal = true;
    pushEvent('FeatureGallery', {
      value: 'Click',
      type: 'Button',
      component: 'UserMenu'
    });
  }

  setShowAsStudent(): void {
    if (this.showAsStudentMode) {
      LocalConfigStore.setShowAsStudent(false);
      /* User disabled ShowAsStudent */
      pushEvent('ShowAsStudent', {
        value: 'Off',
        type: 'Button',
        component: 'UserMenu'
      });
    } else {
      ToastService.showToast(
        this.$i18n.tc('labels.usermenu.display.toast-message')
      );
      LocalConfigStore.setShowAsStudent(true);
      /* User enabled ShowAsStudent */
      pushEvent('ShowAsStudent', {
        value: 'On',
        type: 'Button',
        component: 'UserMenu'
      });
    }
  }

  openSidePanelViewNote(): void {
    this.menuOpen = false;
    SidePanelStore.mode = SidePanelMode.NOTES;
    SidePanelStore.rightPanelIsOpen = true;
    NotesStore.setNotesMode(NotesMode.VIEW);
    NotesStore.notesClickedFrom = NotesClickedFrom.LIST;
    /* User clicked Notes in sidepanel */
    pushEvent('ShowNotesList', {
      type: 'Button',
      component: 'UserMenu'
    });
  }

  openBookmarksView(): void {
    this.menuOpen = false;
    SidePanelStore.rightPanelIsOpen = true;
    SidePanelStore.mode = SidePanelMode.BOOKMARKS;
    /* User clicked Bookmarks in sidepanel */
    pushEvent('ShowBookMarksList', {
      type: 'Button',
      component: 'UserMenu'
    });
  }

  openReadabilitySettings(): void {
    this.menuOpen = false;
    SidePanelStore.mode = SidePanelMode.READABILITY;
    SidePanelStore.rightPanelIsOpen = true;
    /* User clicked text settings in sidepanel */
    pushEvent('ShowTextSettings', {
      type: 'Button',
      component: 'UserMenu'
    });
  }

  openFocusModePrompt(val: any): void {
    this.focusModeSwitch = val;
    if (this.skipPrompt) {
      this.startFocusMode();
    } else {
      this.showFocusModePrompt = val;
    }
  }

  startFocusMode(): void {
    if (this.dontShowAgain && !this.skipPrompt) {
      ClientSettingsStore.rememberToHideFocusModePrompt();
    }
    ClientSettingsStore.changeFocusMode(true);
    this.menuOpen = false;
  }

  resetFocusModePrompt(): void {
    this.showFocusModePrompt = false;
    this.focusModeSwitch = false;
  }

  changeToDarkMode(val: boolean): void {
    ClientSettingsStore.changeDarkMode(val, this.site);
    this.$vuetify.theme.dark = this.currentDarkMode;
  }

  activateCookiesModal(): void {
    CookieStore.showModal = true;
    this.menuOpen = false;
    /* User clicked Cookies in sidepanel */
    pushEvent('ShowCookies', {
      type: 'Button',
      component: 'UserMenu'
    });
  }

  private static detectDarkModeFromOSSettings(): void {
    // https://stackoverflow.com/questions/56393880/how-do-i-detect-dark-mode-using-javascript
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      // this.changeToDarkMode(true); // Disabled for now (MJO)
    }
  }

  getLink(url: string): string {
    return externalTypo3Url(url);
  }

  signOutUrl(url: string): string {
    return getSignOutUrl(url);
  }

  proxyPageAction(name: string) {
    pushEvent(name, {
      type: 'Button',
      component: 'UserMenu'
    });
  }
}
